import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { BUSINESS } from "@/lib/site-data";
import BranchChooser from "./BranchChooser";

const NAV = [
    { id: "about", label: "About" },
    { id: "services", label: "Services" },
    { id: "pricing", label: "Pricing" },
    { id: "gallery", label: "Gallery" },
    { id: "locations", label: "Locations" },
    { id: "contact", label: "Contact" },
];

export default function Header() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [bookingOpen, setBookingOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 30);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const scrollTo = (id) => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        setOpen(false);
    };

    return (
        <>
            <header
                data-testid="site-header"
                className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
                    scrolled
                        ? "bg-cream/85 backdrop-blur-xl border-b border-gold/20 py-3"
                        : "bg-transparent py-5"
                }`}
            >
                <div className="max-w-7xl mx-auto px-5 lg:px-10 flex items-center justify-between">
                    <button
                        onClick={() => scrollTo("hero")}
                        data-testid="header-logo"
                        className="flex items-center gap-3 group"
                    >
                        <span className="w-10 h-10 rounded-full bg-maroon flex items-center justify-center text-gold font-serif text-xl border border-gold/40 group-hover:border-gold transition-all">
                            A
                        </span>
                        <span className="hidden sm:flex flex-col leading-tight text-left">
                            <span className="font-serif text-lg text-maroon tracking-tight">
                                {BUSINESS.name}
                            </span>
                            <span className="text-[0.62rem] tracking-[0.25em] uppercase text-gold-dark">
                                Cape Town
                            </span>
                        </span>
                    </button>

                    <nav className="hidden lg:flex items-center gap-8">
                        {NAV.map((item) => (
                            <button
                                key={item.id}
                                onClick={() => scrollTo(item.id)}
                                data-testid={`nav-${item.id}`}
                                className="text-sm tracking-wide text-ink-soft hover:text-maroon relative group transition-colors"
                            >
                                {item.label}
                                <span className="absolute -bottom-1 left-0 right-0 mx-auto w-0 group-hover:w-full h-px bg-gold transition-all duration-300" />
                            </button>
                        ))}
                    </nav>

                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => setBookingOpen(true)}
                            data-testid="header-book-btn"
                            className="hidden sm:inline-flex btn-gold !py-2.5 !px-5 !text-xs"
                        >
                            Book Now
                        </button>
                        <button
                            onClick={() => setOpen(!open)}
                            data-testid="mobile-menu-toggle"
                            className="lg:hidden w-10 h-10 flex items-center justify-center rounded-full border border-gold/30 text-maroon"
                            aria-label="Toggle menu"
                        >
                            {open ? <X size={18} /> : <Menu size={18} />}
                        </button>
                    </div>
                </div>

                {/* Mobile menu */}
                <div
                    className={`lg:hidden overflow-hidden transition-all duration-500 ${
                        open ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
                    }`}
                >
                    <div className="px-5 pt-4 pb-6 bg-cream/95 backdrop-blur-xl border-t border-gold/20 mt-3 flex flex-col gap-1">
                        {NAV.map((item) => (
                            <button
                                key={item.id}
                                onClick={() => scrollTo(item.id)}
                                data-testid={`mobile-nav-${item.id}`}
                                className="text-left py-3 text-base text-ink-soft border-b border-gold/15 hover:text-maroon transition-colors"
                            >
                                {item.label}
                            </button>
                        ))}
                        <button
                            onClick={() => {
                                setOpen(false);
                                setBookingOpen(true);
                            }}
                            data-testid="mobile-book-btn"
                            className="btn-gold mt-5 justify-center"
                        >
                            Book Now
                        </button>
                    </div>
                </div>
            </header>

            <BranchChooser
                open={bookingOpen}
                onOpenChange={setBookingOpen}
                title="Choose a branch to book"
                description="Connect instantly with our team via WhatsApp."
            />
        </>
    );
}
